import { useCallback, useEffect, useRef, useState } from 'react';
import { getProvinceEvents } from '../api/provinceEvents';
import type { ProvinceEventDto } from '../types/provinceEvents';

/**
 * SF-123 Component 6: page-level hook for the browse page (`ProvinceEventFeed.tsx`) --
 * cursor-paginated (`before`/`limit`), consuming the `{items, nextCursor, hasMore}` envelope
 * (Decision 9b, not a bare array + `length === limit` heuristic).
 *
 * <p>Mark-seen is NOT called from this hook -- Decision 7 pins that responsibility to the page
 * component's own mount effect, keyed on the newest loaded event's `eventSeq` (a stable
 * primitive), so it fires exactly once per real newest-event change.
 *
 * <p>Cancellation guard: a monotonic request counter drops a stale response that resolves after a
 * newer request has already started (e.g. a rapid double "Load older" click), so it can never
 * clobber the newer result.
 */
export function useProvinceEventFeed(
  worldId: string | null,
  provinceId: string | null,
  limit = 50
) {
  const [items, setItems] = useState<ProvinceEventDto[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestIdRef = useRef(0);

  const loadPage = useCallback(
    async (before?: number) => {
      if (!worldId || !provinceId) return;
      const requestId = ++requestIdRef.current;
      if (before == null) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      const result = await getProvinceEvents(worldId, provinceId, { before, limit });

      // A newer request has since started -- drop this now-stale response.
      if (requestId !== requestIdRef.current) return;

      if (result.error) {
        setError(result.error);
      } else if (result.data) {
        setItems((prev) => (before != null ? [...prev, ...result.data!.items] : result.data!.items));
        setNextCursor(result.data.nextCursor);
        setHasMore(result.data.hasMore);
      }

      if (before == null) {
        setIsLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    },
    [worldId, provinceId, limit]
  );

  useEffect(() => {
    void loadPage(undefined);
    // loadPage's identity already reflects worldId/provinceId/limit -- listing it alone here
    // avoids re-running this effect for reasons other than those three actually changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worldId, provinceId, limit]);

  const loadMore = useCallback(() => {
    if (nextCursor != null) {
      void loadPage(nextCursor);
    }
  }, [nextCursor, loadPage]);

  return { items, isLoading, isLoadingMore, error, hasMore, loadMore };
}

/**
 * SF-123 Component 6: the Throne Room card's lightweight one-shot fetch -- `unseen=true` on
 * mount with a caller-supplied `limit` (5 desktop / 3 mobile), no polling (this is a "check when
 * I land on my Throne Room" feed, not a live badge). Never calls the mark-seen endpoint -- Throne
 * Room stays fully read-only (Decision 7); only the browse page's mount advances the cursor.
 */
export function useProvinceEventFeedUnseen(
  worldId: string | null,
  provinceId: string | null,
  limit: number
) {
  const [events, setEvents] = useState<ProvinceEventDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!worldId || !provinceId) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    void (async () => {
      const result = await getProvinceEvents(worldId, provinceId, { unseen: true, limit });
      if (cancelled) return;
      if (result.error) {
        setError(result.error);
      } else if (result.data) {
        setEvents(result.data.items);
      }
      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [worldId, provinceId, limit]);

  return { events, isLoading, error };
}
